import { Eye, Compass, Heart, Target } from "lucide-react";

interface AdviceItem {
  category: string;
  title: string;
  description: string;
}

interface Props {
  advice: AdviceItem[];
}

const PHASE_CONFIG = [
  {
    key: "o1",
    prefix: "o1:",
    label: "o1 — Recognize Differences",
    subtitle: "Immediate awareness actions",
    icon: Eye,
    colorClass: "text-purple-500",
    borderColor: "border-purple-300",
    bgColor: "bg-purple-50",
    dotColor: "bg-purple-400",
    lineColor: "bg-purple-200",
  },
  {
    key: "o2",
    prefix: "o2:",
    label: "o2 — Stabilize & Pause",
    subtitle: "Short-term grounding strategies",
    icon: Heart,
    colorClass: "text-indigo-500",
    borderColor: "border-indigo-300",
    bgColor: "bg-indigo-50",
    dotColor: "bg-indigo-400",
    lineColor: "bg-indigo-200",
  },
  {
    key: "o3",
    prefix: "o3:",
    label: "o3 — New Paradigm Decision",
    subtitle: "Mid-term exploration & decisions",
    icon: Compass,
    colorClass: "text-blue-500",
    borderColor: "border-blue-300",
    bgColor: "bg-blue-50",
    dotColor: "bg-blue-400",
    lineColor: "bg-blue-200",
  },
  {
    key: "o4",
    prefix: "o4:",
    label: "o4 — Integrate New Pattern",
    subtitle: "Long-term paradigm stabilization",
    icon: Target,
    colorClass: "text-emerald-500",
    borderColor: "border-emerald-300",
    bgColor: "bg-emerald-50",
    dotColor: "bg-emerald-400",
    lineColor: "bg-emerald-200",
  },
];

export default function AIAdvicePanel({ advice }: Props) {
  if (!advice || advice.length === 0) return null;

  // Group by o1-o4 phases
  const grouped: Record<string, AdviceItem[]> = {};
  for (const item of advice) {
    const cat = item.category || "";
    const phase = PHASE_CONFIG.find(p => cat.toLowerCase().startsWith(p.prefix));
    const key = phase?.key || "other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <Compass className="h-4 w-4 md:h-5 md:w-5 text-primary" />
        Phase Transition Roadmap
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        Sequential action plan following the o1→o4 consciousness evolution cycle
      </p>

      {/* Vertical timeline */}
      <div className="relative pl-6 md:pl-8 space-y-4 md:space-y-5">
        {PHASE_CONFIG.map((phase, pi) => {
          const items = grouped[phase.key];
          if (!items || items.length === 0) return null;
          const Icon = phase.icon;
          const isLast = pi === PHASE_CONFIG.length - 1 || 
            PHASE_CONFIG.slice(pi + 1).every(p => !grouped[p.key]?.length);

          return (
            <div key={phase.key} className="relative">
              {/* Timeline dot */}
              <div className={`absolute -left-6 md:-left-8 top-0 flex items-center justify-center w-5 h-5 md:w-6 md:h-6 rounded-full ${phase.bgColor} border-2 ${phase.borderColor}`}>
                <Icon className={`h-2.5 w-2.5 md:h-3 md:w-3 ${phase.colorClass}`} />
              </div>
              {/* Timeline line */}
              {!isLast && (
                <div className={`absolute -left-[14px] md:-left-[20px] top-6 md:top-7 bottom-0 w-0.5 ${phase.lineColor}`} />
              )}

              {/* Phase content */}
              <div className={`rounded-lg border ${phase.borderColor} ${phase.bgColor} p-3 md:p-4`}>
                <div className="mb-2">
                  <h4 className={`font-semibold text-xs md:text-sm ${phase.colorClass}`}>{phase.label}</h4>
                  <p className="text-[10px] md:text-xs text-muted-foreground">{phase.subtitle}</p>
                </div>
                <ul className="space-y-1.5 md:space-y-2">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-1.5 md:gap-2">
                      <span className={`mt-1 md:mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${phase.dotColor}`} />
                      <div>
                        <span className="font-medium text-xs md:text-sm text-foreground">{item.title}</span>
                        <p className="text-[10px] md:text-xs text-muted-foreground leading-relaxed mt-0.5">{item.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
